import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, DollarSign, TrendingUp, Target, Award, X, Loader2, Calendar, User, ArrowRight, MessageSquare, Phone, Mail, FileText, Clock } from 'lucide-react';
import api from '../services/api';

interface IndustryVertical {
  id: string;
  name: string;
  category: string;
  description: string;
}

interface Deal {
  id: string;
  name: string;
  description: string;
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  industry: string | null;
  industry_name?: string;
  assigned_to: string | null;
  assigned_to_name?: string;
  stage: string;
  source: string;
  deal_value: string;
  probability: number;
  start_date: string;
  expected_close_date: string | null;
  close_date: string | null;
  lost_reason: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

interface DealActivity {
  id: string;
  deal: string;
  activity_type: string;
  title: string;
  description: string;
  date: string;
  created_at: string;
}

interface PipelineStats {
  total_deals: number;
  total_value: string;
  weighted_value: string;
  win_rate: number;
  by_stage: Array<{ stage: string; count: number; value: string }>;
  avg_deal_size: string;
  avg_days_to_close: number;
}

const STAGES = [
  { value: 'lead', label: 'Lead', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  { value: 'qualified', label: 'Qualified', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'proposal', label: 'Proposal', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
];

const SOURCES = [
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'social', label: 'Social Media' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'other', label: 'Other' },
];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'proposal_sent', label: 'Proposal Sent', icon: FileText },
  { value: 'follow_up', label: 'Follow Up', icon: Clock },
];

const STAGE_PROBABILITIES: Record<string, number> = {
  'lead': 10, 'qualified': 25, 'proposal': 50, 'negotiation': 75, 'closed_won': 100, 'closed_lost': 0,
};

const SalesPipelinePage: React.FC = () => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [stats, setStats] = useState<PipelineStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');

  // Deal modal
  const [showModal, setShowModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{ id: string; first_name: string; last_name: string }>>([]);
  const [verticals, setVerticals] = useState<IndustryVertical[]>([]);
  const [formData, setFormData] = useState({
    name: '', description: '', company_name: '', contact_name: '', contact_email: '', contact_phone: '',
    assigned_to: '', industry: '', stage: 'lead',
    source: 'other', deal_value: '', probability: 10,
    start_date: new Date().toISOString().split('T')[0],
    expected_close_date: '', notes: '',
  });

  // Detail panel
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [activities, setActivities] = useState<DealActivity[]>([]);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityForm, setActivityForm] = useState({ activity_type: 'note', title: '', description: '', date: new Date().toISOString().split('T')[0] });

  // Lost reason modal
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostDealId, setLostDealId] = useState<string | null>(null);
  const [lostReason, setLostReason] = useState('');

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getDeals({
        stage: selectedStage !== 'all' ? selectedStage : undefined,
        source: selectedSource !== 'all' ? selectedSource : undefined,
        search: searchQuery || undefined,
      });
      setDeals(response.results || response);
      setError(null);
    } catch (err) {
      setError('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [selectedStage, selectedSource, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await api.getPipelineStats();
      setStats(data);
    } catch (err) {
      // Stats are non-critical
    }
  }, []);

  const fetchVerticals = useCallback(async () => {
    try {
      const response = await api.getIndustries();
      setVerticals((response.results || response) as any);
    } catch (err) { /* non-critical */ }
  }, []);
  const fetchTeam = useCallback(async () => {
    try {
      const response = await api.getTeamMembers();
      setTeamMembers(response.results || response);
    } catch (err) { /* non-critical */ }
  }, []);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);
  useEffect(() => { fetchStats(); fetchTeam(); fetchVerticals(); }, [fetchStats, fetchTeam, fetchVerticals]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        deal_value: formData.deal_value || '0',
        assigned_to: formData.assigned_to || null,
        industry: formData.industry || null,
        expected_close_date: formData.expected_close_date || null,
        contact_email: formData.contact_email || '',
        contact_phone: formData.contact_phone || '',
      };
      if (editingDeal) {
        await api.updateDeal(editingDeal.id, data);
      } else {
        await api.createDeal(data);
      }
      setShowModal(false);
      setEditingDeal(null);
      resetForm();
      fetchDeals();
      fetchStats();
    } catch (err) {
      alert('Failed to save deal');
    }
  };

  const handleEdit = (deal: Deal) => {
    setEditingDeal(deal);
    setFormData({
      name: deal.name,
      description: deal.description,
      company_name: deal.company_name || '',
      contact_name: deal.contact_name || '',
      contact_email: deal.contact_email || '',
      contact_phone: deal.contact_phone || '',
      assigned_to: deal.assigned_to || '',
      industry: deal.industry || '',
      stage: deal.stage,
      source: deal.source,
      deal_value: deal.deal_value,
      probability: deal.probability,
      start_date: deal.start_date,
      expected_close_date: deal.expected_close_date || '',
      notes: deal.notes,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Delete this item?')) {
      try {
        await api.deleteDeal(id);
        if (selectedDeal?.id === id) setSelectedDeal(null);
        fetchDeals();
        fetchStats();
      } catch (err) {
        alert('Failed to delete deal');
      }
    }
  };

  const handleMoveStage = async (dealId: string, newStage: string) => {
    if (newStage === 'closed_lost') {
      setLostDealId(dealId);
      setLostReason('');
      setShowLostModal(true);
      return;
    }
    try {
      await api.moveDealStage(dealId, newStage);
      fetchDeals();
      fetchStats();
      if (selectedDeal?.id === dealId) {
        setSelectedDeal({ ...selectedDeal, stage: newStage, probability: STAGE_PROBABILITIES[newStage] || selectedDeal.probability });
      }
    } catch (err) {
      alert('Failed to move deal');
    }
  };

  const handleConfirmLost = async () => {
    if (!lostDealId) return;
    try {
      await api.moveDealStage(lostDealId, 'closed_lost', lostReason);
      setShowLostModal(false);
      setLostDealId(null);
      setLostReason('');
      fetchDeals();
      fetchStats();
    } catch (err) {
      alert('Failed to update deal');
    }
  };

  const handleSelectDeal = async (deal: Deal) => {
    setSelectedDeal(deal);
    try {
      const response = await api.getDealActivities(deal.id);
      setActivities(response.results || response);
    } catch (err) {
      setActivities([]);
    }
  };

  const handleAddActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeal) return;
    try {
      await api.createDealActivity(selectedDeal.id, activityForm);
      const response = await api.getDealActivities(selectedDeal.id);
      setActivities(response.results || response);
      setShowActivityForm(false);
      setActivityForm({ activity_type: 'note', title: '', description: '', date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      alert('Failed to add activity');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', company_name: '', contact_name: '', contact_email: '', contact_phone: '',
      assigned_to: '', industry: '', stage: 'lead',
      source: 'other', deal_value: '', probability: 10,
      start_date: new Date().toISOString().split('T')[0],
      expected_close_date: '', notes: '',
    });
  };

  const isLeadStage = (stage: string) => stage === 'lead';
  const isDealStage = (stage: string) => !isLeadStage(stage);
  const getItemLabel = (stage: string) => isLeadStage(stage) ? 'Lead' : 'Internal Lead';

  const getStageInfo = (stage: string) => STAGES.find(s => s.value === stage) || STAGES[0];
  const getSourceLabel = (source: string) => SOURCES.find(s => s.value === source)?.label || source;

  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
  };

  const getNextStages = (currentStage: string): Array<{ value: string; label: string }> => {
    const order = ['lead', 'qualified', 'proposal', 'negotiation', 'closed_won'];
    const currentIndex = order.indexOf(currentStage);
    if (currentIndex === -1 || currentStage === 'closed_won' || currentStage === 'closed_lost') return [];
    const next: Array<{ value: string; label: string }> = [];
    if (currentIndex + 1 < order.length) {
      const nextStage = order[currentIndex + 1];
      const info = getStageInfo(nextStage);
      next.push({ value: nextStage, label: info.label });
    }
    next.push({ value: 'closed_lost', label: 'Lost' });
    return next;
  };

  if (loading && deals.length === 0) {
    return <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-white">Internal Leads</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Track leads and deals</p>
            </div>
            <button onClick={() => { resetForm(); setEditingDeal(null); setShowModal(true); }} className="btn btn-primary">
              <Plus className="w-4 h-4" />Add Lead
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
            <div className="panel p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded"><Target className="w-4 h-4 text-blue-600 dark:text-blue-400" /></div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Total Internal Leads</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats.total_deals}</p>
                </div>
              </div>
            </div>
            <div className="panel p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded"><DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" /></div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Pipeline Value</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(stats.total_value)}</p>
                </div>
              </div>
            </div>
            <div className="panel p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-purple-100 dark:bg-purple-900/30 rounded"><TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" /></div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Weighted Value</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(stats.weighted_value)}</p>
                </div>
              </div>
            </div>
            <div className="panel p-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded"><Award className="w-4 h-4 text-amber-600 dark:text-amber-400" /></div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Win Rate</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{stats.win_rate}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && <div className="bg-status-error/10 border border-status-error/30 rounded p-2 text-xs text-status-error mb-2">{error}</div>}

        {/* Filters */}
        <div className="filter-bar mb-2">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input type="text" placeholder="Search leads and deals..." className="input pl-7" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
          <select className="select" value={selectedStage} onChange={(e) => setSelectedStage(e.target.value)}>
            <option value="all">All Stages</option>
            {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select className="select" value={selectedSource} onChange={(e) => setSelectedSource(e.target.value)}>
            <option value="all">All Sources</option>
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Stage Summary */}
        {stats && (
          <div className="flex flex-wrap gap-1 mb-3">
            {STAGES.filter(s => s.value !== 'closed_lost').map(stage => (
              <button
                key={stage.value}
                onClick={() => setSelectedStage(selectedStage === stage.value ? 'all' : stage.value)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-all ${
                  selectedStage === stage.value ? stage.color + ' ring-2 ring-offset-1 ring-gray-400 dark:ring-gray-500' : 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span>{stage.label}</span>
                <span className="font-bold ml-2">{(stats.by_stage.find(s => s.stage === stage.value)?.count || 0)}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-3">
          {/* Deals List */}
          <div className={`${selectedDeal ? 'w-1/2' : 'w-full'} transition-all`}>
            {deals.length === 0 ? (
              <div className="text-center py-8"><p className="text-xs text-gray-500 dark:text-gray-400">No leads or deals found</p></div>
            ) : (
              <div className="grid grid-cols-1 gap-2">
                {deals.map((deal) => {
                  const stageInfo = getStageInfo(deal.stage);
                  const nextStages = getNextStages(deal.stage);
                  return (
                    <div
                      key={deal.id}
                      className={`panel cursor-pointer transition-all ${selectedDeal?.id === deal.id ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}
                      onClick={() => handleSelectDeal(deal)}
                    >
                      <div className="p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{deal.name}</h3>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${stageInfo.color}`}>
                                {stageInfo.label}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{deal.company_name || deal.contact_name || 'No contact'}{deal.industry_name && <span className="ml-1 text-gray-400">· {deal.industry_name}</span>}</p>
                          </div>
                          {isDealStage(deal.stage) ? (
                          <div className="text-right ml-2">
                            <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(deal.deal_value)}</p>
                            <p className="text-xs text-gray-500">{deal.probability}% prob</p>
                          </div>
                          ) : (
                          <div className="text-right ml-2">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">{getSourceLabel(deal.source)}</span>
                          </div>
                          )}
                        </div>

                        <div className="grid grid-cols-3 gap-2 mb-2 text-xs">
                          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <User className="w-3 h-3" />
                            <span className="truncate">{deal.assigned_to_name || 'Unassigned'}</span>
                          </div>
                          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                            <MessageSquare className="w-3 h-3" />
                            <span>{getSourceLabel(deal.source)}</span>
                          </div>
                          {deal.expected_close_date && (
                            <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(deal.expected_close_date).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>

                        {/* Stage progression actions */}
                        {nextStages.length > 0 && (
                          <div className="flex gap-1 border-t border-light-border dark:border-dark-border pt-2">
                            {nextStages.map(ns => (
                              <button
                                key={ns.value}
                                onClick={(e) => { e.stopPropagation(); handleMoveStage(deal.id, ns.value); }}
                                className={`btn btn-ghost text-xs flex items-center gap-1 ${ns.value === 'closed_lost' ? 'text-red-600' : 'text-blue-600'}`}
                              >
                                <ArrowRight className="w-3 h-3" />{ns.label}
                              </button>
                            ))}
                            <div className="flex-1" />
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(deal); }} className="btn btn-ghost text-xs text-gray-600">Edit</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(deal.id); }} className="btn btn-ghost text-xs text-red-600">Delete</button>
                          </div>
                        )}
                        {nextStages.length === 0 && (
                          <div className="flex justify-end gap-1 border-t border-light-border dark:border-dark-border pt-2">
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(deal); }} className="btn btn-ghost text-xs text-gray-600">Edit</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(deal.id); }} className="btn btn-ghost text-xs text-red-600">Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selectedDeal && (
            <div className="w-1/2">
              <div className="panel sticky top-3">
                <div className="p-3 border-b border-light-border dark:border-dark-border">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{selectedDeal.name}</h2>
                      <p className="text-xs text-gray-500">{selectedDeal.company_name}</p>
                    </div>
                    <button onClick={() => setSelectedDeal(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
                  {/* Deal details */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Value</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(selectedDeal.deal_value)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Probability</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedDeal.probability}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Stage</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStageInfo(selectedDeal.stage).color}`}>
                        {getStageInfo(selectedDeal.stage).label}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Source</p>
                      <p className="font-medium text-gray-900 dark:text-white">{getSourceLabel(selectedDeal.source)}</p>
                    </div>
                    {selectedDeal.industry_name && (
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Industry</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedDeal.industry_name}</p>
                    </div>
                    )}
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Start Date</p>
                      <p className="text-gray-900 dark:text-white">{new Date(selectedDeal.start_date).toLocaleDateString()}</p>
                    </div>
                    {selectedDeal.expected_close_date && (
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Expected Close</p>
                        <p className="text-gray-900 dark:text-white">{new Date(selectedDeal.expected_close_date).toLocaleDateString()}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Assigned To</p>
                      <p className="text-gray-900 dark:text-white">{selectedDeal.assigned_to_name || 'Unassigned'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Weighted Value</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(parseFloat(selectedDeal.deal_value) * selectedDeal.probability / 100)}</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  {(selectedDeal.contact_name || selectedDeal.contact_email || selectedDeal.contact_phone) && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contact</p>
                      <div className="space-y-1 text-xs">
                        {selectedDeal.contact_name && <p className="text-gray-900 dark:text-white">{selectedDeal.contact_name}</p>}
                        {selectedDeal.contact_email && (
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <Mail className="w-3 h-3" /><span>{selectedDeal.contact_email}</span>
                          </div>
                        )}
                        {selectedDeal.contact_phone && (
                          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                            <Phone className="w-3 h-3" /><span>{selectedDeal.contact_phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedDeal.description && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Description</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">{selectedDeal.description}</p>
                    </div>
                  )}

                  {selectedDeal.notes && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</p>
                      <p className="text-xs text-gray-700 dark:text-gray-300">{selectedDeal.notes}</p>
                    </div>
                  )}

                  {selectedDeal.lost_reason && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded p-2">
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium mb-1">Lost Reason</p>
                      <p className="text-xs text-red-700 dark:text-red-300">{selectedDeal.lost_reason}</p>
                    </div>
                  )}

                  {/* Activities */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-900 dark:text-white">Activity Log</p>
                      <button onClick={() => setShowActivityForm(!showActivityForm)} className="btn btn-ghost text-xs text-blue-600">
                        <Plus className="w-3 h-3" />Add
                      </button>
                    </div>

                    {showActivityForm && (
                      <form onSubmit={handleAddActivity} className="bg-gray-50 dark:bg-gray-800 rounded p-2 mb-2 space-y-2">
                        <select value={activityForm.activity_type} onChange={(e) => setActivityForm({ ...activityForm, activity_type: e.target.value })} className="select text-xs">
                          {ACTIVITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input type="text" required placeholder="Title" value={activityForm.title} onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })} className="input text-xs" />
                        <textarea placeholder="Details..." value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} className="input text-xs" rows={2} />
                        <input type="date" value={activityForm.date} onChange={(e) => setActivityForm({ ...activityForm, date: e.target.value })} className="input text-xs" />
                        <div className="flex justify-end gap-1">
                          <button type="button" onClick={() => setShowActivityForm(false)} className="btn btn-secondary text-xs">Cancel</button>
                          <button type="submit" className="btn btn-primary text-xs">Save</button>
                        </div>
                      </form>
                    )}

                    {activities.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-3">No activities yet</p>
                    ) : (
                      <div className="space-y-2">
                        {activities.map((activity) => {
                          const typeInfo = ACTIVITY_TYPES.find(t => t.value === activity.activity_type);
                          const IconComponent = typeInfo?.icon || FileText;
                          return (
                            <div key={activity.id} className="flex gap-2 text-xs">
                              <div className="p-1 bg-gray-100 dark:bg-gray-800 rounded h-fit mt-0.5">
                                <IconComponent className="w-3 h-3 text-gray-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 dark:text-white">{activity.title}</p>
                                {activity.description && <p className="text-gray-500 dark:text-gray-400 mt-0.5">{activity.description}</p>}
                                <p className="text-gray-400 mt-0.5">{new Date(activity.date).toLocaleDateString()}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Deal Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{editingDeal ? `Edit ${getItemLabel(formData.stage)}` : `Add ${getItemLabel(formData.stage)}`}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-3 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input" placeholder={isLeadStage(formData.stage) ? 'e.g., Acme Corp - Inbound Inquiry' : 'e.g., Website Redesign - Acme Corp'} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
                <input type="text" value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} className="input" placeholder="e.g., Acme Corp" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Name</label>
                  <input type="text" value={formData.contact_name} onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })} className="input" placeholder="e.g., John Smith" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="input" placeholder="john@acme.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                <input type="text" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="input" placeholder="+1 (555) 000-0000" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Industry</label>
                <select value={formData.industry} onChange={(e) => setFormData({ ...formData, industry: e.target.value })} className="select">
                  <option value="">Select industry...</option>
                  {Object.entries(verticals.reduce((acc: Record<string, IndustryVertical[]>, v) => {
                    const cat = v.category || "other";
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(v);
                    return acc;
                  }, {})).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                    <optgroup key={category} label={category.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}>
                      {items.sort((a, b) => a.name.localeCompare(b.name)).map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Stage</label>
                  <select value={formData.stage} onChange={(e) => {
                    const newStage = e.target.value;
                    setFormData({ ...formData, stage: newStage, probability: STAGE_PROBABILITIES[newStage] || formData.probability });
                  }} className="select">
                    {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Source</label>
                  <select value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} className="select">
                    {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              {isDealStage(formData.stage) && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Value ($)</label>
                  <input type="number" step="0.01" min="0" value={formData.deal_value} onChange={(e) => setFormData({ ...formData, deal_value: e.target.value })} className="input" placeholder="0" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Probability (%)</label>
                  <input type="number" min="0" max="100" value={formData.probability} onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) || 0 })} className="input" />
                </div>
              </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Assigned To</label>
                <select value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })} className="select">
                  <option value="">Unassigned</option>
                  {teamMembers.map(m => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                </select>
              </div>
              <div className={isDealStage(formData.stage) ? 'grid grid-cols-2 gap-2' : ''}>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                  <input type="date" required value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="input" />
                </div>
                {isDealStage(formData.stage) && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Close</label>
                  <input type="date" value={formData.expected_close_date} onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })} className="input" />
                </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input" rows={2} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input" rows={2} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingDeal ? 'Update' : `Create ${getItemLabel(formData.stage)}`}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lost Reason Modal */}
      {showLostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-sm w-full border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between p-3 border-b border-light-border dark:border-dark-border">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Mark as Lost</h2>
              <button onClick={() => setShowLostModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 space-y-2">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Why was this deal lost?</label>
              <textarea value={lostReason} onChange={(e) => setLostReason(e.target.value)} className="input" rows={3} placeholder="e.g., Budget constraints, went with competitor..." />
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowLostModal(false)} className="btn btn-secondary">Cancel</button>
                <button onClick={handleConfirmLost} className="btn btn-primary bg-red-600 hover:bg-red-700 border-red-600">Mark Lost</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPipelinePage;
